const STORAGE_KEY = 'ss_attribution_source';
const MAX_LENGTH = 64;
const VALID_PATTERN = /^[A-Za-z0-9_-]+$/;

/**
 * Sanitizes an attribution value: trims, validates characters, enforces max length.
 * @returns The sanitized value, or null if invalid.
 */
export const sanitizeAttribution = (value: string): string | null => {
    const trimmed = value.trim();
    if (!trimmed || trimmed.length > MAX_LENGTH || !VALID_PATTERN.test(trimmed)) {
        return null;
    }
    return trimmed;
};

/**
 * Reads `source` (preferred) or `utm_source` (fallback) from URL search params and sanitizes.
 */
export const parseAttribution = (searchParams: URLSearchParams): string | null => {
    const raw = searchParams.get('source') || searchParams.get('utm_source');
    if (!raw) return null;
    return sanitizeAttribution(raw);
};

/**
 * Stores attribution in sessionStorage. Overwrites any existing value (tab-scoped).
 */
export const storeAttribution = (value: string): void => {
    sessionStorage.setItem(STORAGE_KEY, value);
};

/**
 * Reads stored attribution from sessionStorage.
 */
export const getStoredAttribution = (): string | null => {
    return sessionStorage.getItem(STORAGE_KEY);
};

/**
 * Resolves attribution: prefers router state, falls back to sessionStorage.
 * Always re-sanitizes the resolved value for defense in depth.
 */
export const resolveAttribution = (routerState?: { attribution_source?: string }): string | null => {
    const source = routerState?.attribution_source || getStoredAttribution();
    if (!source) {
        return null;
    }
    return sanitizeAttribution(source);
};
